import React from 'react';
import { 
  LineChart,
  Gauge,
  TrendingUp,
  Building2,
  Leaf,
  CheckCircle,
  Phone
} from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import img from "../../Images/energy.webp"
import img1 from "../../Images/energy1.png"

const EnergyAuditPage = () => {
  const navigate = useNavigate();
  
  const services = [
    {
      title: "Comprehensive Energy Assessment",
      description: "Detailed analysis of energy consumption patterns, identifying inefficiencies and potential savings opportunities in industrial systems.",
      icon: <Gauge className="w-6 h-6 sm:w-8 sm:h-8 text-green-600" />
    },
    {
      title: "Steam System Optimization",
      description: "Specialized evaluation of boiler efficiency, steam distribution, and condensate recovery systems to maximize thermal efficiency.",
      icon: <TrendingUp className="w-6 h-6 sm:w-8 sm:h-8 text-green-600" />
    },
    {
      title: "Sustainability Consulting",
      description: "Expert guidance on implementing eco-friendly solutions and achieving compliance with environmental regulations.",
      icon: <Leaf className="w-6 h-6 sm:w-8 sm:h-8 text-green-600" />
    }
  ];

  const processSteps = [
    {
      title: "Initial Assessment",
      description: "Preliminary evaluation of your facility's energy consumption patterns"
    },
    {
      title: "Data Collection",
      description: "Comprehensive gathering of energy usage data and system performance metrics"
    },
    {
      title: "Analysis & Modeling",
      description: "Advanced analysis to identify optimization opportunities"
    },
    {
      title: "Implementation Plan",
      description: "Detailed recommendations with ROI calculations and action steps"
    }
  ];

  // Generate structured data for SEO
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Industrial Energy Audit Services",
    "description": "Comprehensive energy audit solutions for industrial facilities including energy assessment, steam system optimization, and sustainability consulting",
    "provider": {
      "@type": "Organization",
      "name": "Gortherm Energy",
      "url": "https://www.gorthermenergy.com"
    },
    "areaServed": {
      "@type": "Country",
      "name": "Nepal"
    },
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Energy Audit Services",
      "itemListElement": services.map((service, index) => ({
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": service.title,
          "description": service.description
        }
      }))
    }
  };

  return (
    <>
      <Helmet>
        <title>Industrial Energy Audit Services | Energy Efficiency Consulting | Gortherm Energy</title>
        <meta name="description" content="Professional industrial energy audit services in Nepal. Comprehensive energy assessments, steam system optimization, and sustainability consulting for improved efficiency and cost savings. Expert energy auditors for industrial facilities." />
        <meta name="keywords" content="energy audit, industrial energy audit, energy efficiency consulting, steam system optimization, energy assessment, sustainability consulting, energy audit Nepal, boiler efficiency audit, industrial energy management, energy saving solutions" />
        
        {/* Open Graph */}
        <meta property="og:title" content="Industrial Energy Audit Services | Gortherm Energy" />
        <meta property="og:description" content="Nepal's trusted partner for comprehensive energy optimization and sustainability solutions. Professional energy audits for industrial facilities." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.gorthermenergy.com/energy-audit" />
        <meta property="og:image" content={img1} />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Industrial Energy Audit Services | Gortherm Energy" />
        <meta name="twitter:description" content="Comprehensive energy audit solutions for industrial energy optimization and sustainability." />
        <meta name="twitter:image" content={img1} />
        
        {/* Additional SEO */}
        <link rel="canonical" href="https://www.gorthermenergy.com/energy-audit" />
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
        <meta name="author" content="Gortherm Energy" />
        <meta name="language" content="English" />
        <meta name="geo.region" content="NP" />
        <meta name="geo.placename" content="Nepal" />
        
        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      <main className="w-full">
        {/* Hero Section */}
        <header className="relative h-64 sm:h-80 lg:h-96 bg-gradient-to-r from-green-800 to-emerald-800 overflow-hidden pt-16 sm:pt-20">
          <div className="absolute inset-0">
            <img
              src={img1}
              alt="Industrial energy audit and efficiency consulting services by Gortherm Energy"
              className="w-full h-full object-cover opacity-20"
              loading="eager"
              width="1920"
              height="400"
            />
          </div>
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex items-center">
            <div className="text-white max-w-3xl">
              <h1 className="text-2xl sm:text-4xl lg:text-5xl font-bold mb-3 sm:mb-4">
                Industrial Energy Audit Services
              </h1>
              <p className="text-base sm:text-xl lg:text-2xl mb-6 sm:mb-8">
                Nepal's trusted partner for comprehensive energy optimization and sustainability solutions.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                <button 
                  className="bg-green-500 hover:bg-green-600 active:bg-green-700 text-white px-5 sm:px-6 py-2.5 sm:py-3 rounded-lg flex items-center justify-center gap-2 transition-all text-sm sm:text-base font-semibold"
                  onClick={() => navigate('/contact')}
                  aria-label="Request an energy audit"
                >
                  Request an Audit
                  <Building2 className="w-4 h-4 sm:w-5 sm:h-5" aria-hidden="true" />
                </button>
              </div>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          {/* Services Grid */}
          <section className="text-center mb-12 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl font-bold mb-4 sm:mb-6">Our Energy Audit Services</h2>
            <p className="text-sm sm:text-base text-gray-600 max-w-3xl mx-auto px-4">
              Gortherm Energy provides comprehensive energy audit solutions backed by decades of 
              experience in industrial systems and cutting-edge analysis techniques.
            </p>
          </section>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8 mb-12 sm:mb-16">
            {services.map((service, index) => (
              <article 
                key={index}
                className="p-4 sm:p-6 rounded-xl border border-gray-200 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
              >
                <div className="mb-4" aria-hidden="true">
                  {service.icon}
                </div>
                <h3 className="text-lg sm:text-xl font-semibold mb-2 sm:mb-3">{service.title}</h3>
                <p className="text-sm sm:text-base text-gray-600">{service.description}</p>
              </article>
            ))}
          </div>

          {/* Process Timeline */}
          <section className="mb-12 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl font-bold mb-8 sm:mb-12 text-center">Our Audit Process</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6 sm:gap-8">
              {processSteps.map((step, index) => (
                <div key={index} className="relative">
                  <div className="flex flex-col items-center">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 bg-green-100 rounded-full flex items-center justify-center mb-3 sm:mb-4">
                      <span className="text-green-600 font-bold text-sm sm:text-base">{index + 1}</span>
                    </div>
                    <h3 className="text-base sm:text-lg font-semibold mb-2 text-center">{step.title}</h3>
                    <p className="text-xs sm:text-sm text-gray-600 text-center">{step.description}</p>
                  </div>
                  {index < processSteps.length - 1 && (
                    <div className="hidden md:block absolute top-6 left-[60%] w-[80%] h-[2px] bg-green-100"></div>
                  )}
                </div>
              ))}
            </div>
          </section>

          {/* Technical Expertise */}
          <section className="grid grid-cols-1 md:grid-cols-2 gap-8 sm:gap-12 items-center mb-12 sm:mb-16">
            <div className="relative h-64 sm:h-80 md:h-96 order-2 md:order-1">
              <img 
                src={img}
                alt="Professional energy audit process and industrial energy efficiency analysis"
                className="rounded-lg shadow-xl object-cover h-full w-full"
                loading="lazy"
                width="600"
                height="400"
              />
            </div>
            <div className="order-1 md:order-2">
              <h2 className="text-2xl sm:text-3xl font-bold mb-4 sm:mb-6">Why Choose Gortherm Energy</h2>
              <div className="space-y-4 sm:space-y-6">
                <div className="flex items-start gap-3 sm:gap-4">
                  <div className="p-2 bg-green-100 rounded-lg flex-shrink-0">
                    <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6 text-green-600" aria-hidden="true" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1 sm:mb-2 text-base sm:text-lg">Industry Leadership</h3>
                    <p className="text-sm sm:text-base text-gray-600">
                      Nepal's leading provider of industrial energy solutions with expertise in boilers, steam systems, and water treatment.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3 sm:gap-4">
                  <div className="p-2 bg-green-100 rounded-lg flex-shrink-0">
                    <LineChart className="w-5 h-5 sm:w-6 sm:h-6 text-green-600" aria-hidden="true" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1 sm:mb-2 text-base sm:text-lg">Data-Driven Approach</h3>
                    <p className="text-sm sm:text-base text-gray-600">
                      Advanced analytics and monitoring systems to provide actionable insights for energy optimization.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3 sm:gap-4">
                  <div className="p-2 bg-green-100 rounded-lg flex-shrink-0">
                    <TrendingUp className="w-5 h-5 sm:w-6 sm:h-6 text-green-600" aria-hidden="true" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1 sm:mb-2 text-base sm:text-lg">Proven Results</h3>
                    <p className="text-sm sm:text-base text-gray-600">
                      Track record of helping industrial facilities achieve 15-30% energy savings through systematic optimization.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Benefits Section */}
          <section className="bg-green-50 rounded-2xl p-6 sm:p-8 lg:p-12 mb-12 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl font-bold mb-6 sm:mb-8 text-center">Benefits of Energy Audits</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8">
              <div>
                <h3 className="text-lg sm:text-xl font-semibold mb-2 text-green-700">Cost Reduction</h3>
                <p className="text-sm sm:text-base text-gray-700">
                  Identify and eliminate energy waste, reducing operational costs by 15-30% through targeted improvements.
                </p>
              </div>
              <div>
                <h3 className="text-lg sm:text-xl font-semibold mb-2 text-green-700">Environmental Impact</h3>
                <p className="text-sm sm:text-base text-gray-700">
                  Lower carbon footprint and meet sustainability goals while improving environmental compliance.
                </p>
              </div>
              <div>
                <h3 className="text-lg sm:text-xl font-semibold mb-2 text-green-700">System Reliability</h3>
                <p className="text-sm sm:text-base text-gray-700">
                  Improve equipment performance and reduce downtime through optimized operation and maintenance.
                </p>
              </div>
              <div>
                <h3 className="text-lg sm:text-xl font-semibold mb-2 text-green-700">Strategic Planning</h3>
                <p className="text-sm sm:text-base text-gray-700">
                  Develop long-term energy management strategies with clear ROI and implementation roadmaps.
                </p>
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <aside className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl p-6 sm:p-8 lg:p-12 shadow-lg text-center">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Ready to Optimize Your Energy Usage?
            </h2>
            <p className="text-base sm:text-lg text-gray-700 mb-6 sm:mb-8 max-w-2xl mx-auto">
              Schedule a comprehensive energy audit with our experts and start saving on energy costs today.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => navigate('/contact')}
                className="bg-green-600 hover:bg-green-700 active:bg-green-800 text-white px-6 sm:px-8 lg:px-10 py-3 sm:py-4 rounded-lg font-semibold text-sm sm:text-base lg:text-lg transition-all duration-300 shadow-md hover:shadow-xl"
              >
                Request Energy Audit
              </button>
              <a 
                href="tel:+9779802781400"
                className="inline-block"
                aria-label="Contact energy audit experts at +977 980 278 1400"
              >
                <button className="bg-white hover:bg-gray-50 text-green-600 border-2 border-green-600 px-6 sm:px-8 lg:px-10 py-3 sm:py-4 rounded-lg flex items-center gap-2 sm:gap-3 mx-auto transition-all duration-300 shadow-md hover:shadow-xl text-sm sm:text-base lg:text-lg font-semibold">
                  <Phone className="h-5 w-5 sm:h-6 sm:w-6" aria-hidden="true" />
                  <span>Call Our Experts</span>
                </button>
              </a>
            </div>
            
          </aside>
        </div>
      </main>
    </>
  );
};

export default EnergyAuditPage;